import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '@yorukiri/i18n';
import { colors } from '@yorukiri/ui/tokens';
import { Sparkles } from './icons';

// Compact button that expands into an Anki-style "Custom study" menu. Four
// canned modes that cover what learners actually want outside the normal due
// queue: cram, look ahead, preview new, drill what they failed.
//
// The menu is rendered via React Portal with fixed positioning anchored to the
// trigger rect, so it's never clipped by the scrolling right panel or its
// stacking context. Recomputes on scroll/resize so the popover tracks the
// trigger as the panel scrolls.
type Mode = {
  id: 'cram' | 'ahead' | 'new' | 'failed';
  title: string;
  description: string;
  params: Record<string, string>;
};

const MENU_WIDTH = 264;

export function ExtraReviewMenu({ pageId }: { pageId: string }) {
  const { t } = useTranslation('page');
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const recompute = () => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const viewportW = window.innerWidth;
    let left = rect.right - MENU_WIDTH;
    if (left < 8) left = 8;
    if (left + MENU_WIDTH > viewportW - 8) left = viewportW - 8 - MENU_WIDTH;
    setPos({ top: rect.bottom + 6, left });
  };

  useLayoutEffect(() => {
    if (!open) return;
    recompute();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onScroll = () => recompute();
    const onResize = () => recompute();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        menuRef.current &&
        !menuRef.current.contains(target) &&
        !triggerRef.current?.contains(target)
      ) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const modes: Mode[] = [
    {
      id: 'cram',
      title: t('extra.cram_title'),
      description: t('extra.cram_desc'),
      params: { mode: 'cram' },
    },
    {
      id: 'ahead',
      title: t('extra.ahead_title'),
      description: t('extra.ahead_desc'),
      params: { mode: 'ahead', days: '7' },
    },
    {
      id: 'new',
      title: t('extra.new_title'),
      description: t('extra.new_desc'),
      params: { mode: 'new', limit: '20' },
    },
    {
      id: 'failed',
      title: t('extra.failed_title'),
      description: t('extra.failed_desc'),
      params: { mode: 'failed' },
    },
  ];

  const launch = (m: Mode) => {
    const qs = new URLSearchParams(m.params).toString();
    navigate(`/review/${pageId}?${qs}`);
    setOpen(false);
  };

  return (
    <>
      <button
        ref={triggerRef}
        onClick={() => setOpen((v) => !v)}
        title={t('right_panel.extra_review')}
        aria-label={t('right_panel.extra_review')}
        style={{
          width: 32,
          height: 34,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: open ? colors.accentSoft : 'transparent',
          color: open ? colors.accent : colors.ink2,
          border: `1px solid ${open ? colors.accent : colors.line2}`,
          borderRadius: 7,
          cursor: 'pointer',
          padding: 0,
        }}
      >
        <Sparkles size={13} />
      </button>

      {open && pos
        ? createPortal(
            <div
              ref={menuRef}
              style={{
                position: 'fixed',
                top: pos.top,
                left: pos.left,
                width: MENU_WIDTH,
                background: colors.surface,
                border: `1px solid ${colors.line2}`,
                borderRadius: 8,
                boxShadow: '0 12px 40px -14px rgba(24,22,15,0.28)',
                zIndex: 1000,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  padding: '8px 12px',
                  fontSize: 10,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: colors.ink3,
                  borderBottom: `1px solid ${colors.line}`,
                }}
              >
                {t('right_panel.extra_review')}
              </div>
              {modes.map((m) => (
                <button
                  key={m.id}
                  onClick={() => launch(m)}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    background: 'transparent',
                    border: 'none',
                    borderBottom: `1px solid ${colors.line}`,
                    padding: '10px 12px',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = colors.surface2;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <div style={{ fontSize: 13, fontWeight: 500, color: colors.ink }}>
                    {m.title}
                  </div>
                  <div style={{ fontSize: 11.5, color: colors.ink3, marginTop: 2 }}>
                    {m.description}
                  </div>
                </button>
              ))}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
